/**
 * Advisory Polling
 *
 * The local-mode polling fallback for the one user-mutable Strategic
 * Advisory channel that still has a client write path: `keyQuestions`.
 *
 * (a) Why this exists at all: the Firebase emulator's `onSnapshot` may not
 *     fire for a write made from a separate process. agent-svc runs as its
 *     own process against the same emulator, so a live local supervisor
 *     turn that writes `keyQuestions` can leave a page whose only signal is
 *     `onSnapshot` stale indefinitely. `startLocalPoll` below is the
 *     workaround, mirroring the only existing local-mode poll precedent in
 *     this codebase, `chatListeners.ts:127-178`.
 *
 * (b) The `goal` and `engagements` channels keep plain `onSnapshot` and are
 *     deliberately NOT polled. Both are exposed to the identical gap
 *     agent-svc writes can trigger — that exposure is real and accepted, a
 *     known issue rather than something silently covered here.
 *
 * (c) There is no `criteria` channel — this module only ever polls
 *     `keyQuestions`. The recently-mutated guard machinery below
 *     (`GUARD_MAX_PENDING_MS`, `GUARD_TTL_MS`) has no current production
 *     caller (see `advisoryThunks.ts`'s own note on why it is kept as
 *     generic infrastructure) but is still exercised by this module's own
 *     tests.
 *
 * `POLL_INTERVAL_MS` matches `chatListeners.ts:135`'s established local-mode
 * precedent (2000ms). `GUARD_TTL_MS` is roughly two poll intervals — the
 * recently-mutated guard (`advisorySlice.ts`) must outlive at least one
 * full poll tick so an in-flight optimistic removal (or an in-flight add
 * whose write hasn't landed yet) is never wiped or resurrected by a poll
 * that fires before the write completes.
 */

/** Matches `chatListeners.ts:135`, the established local-mode poll interval. */
export const POLL_INTERVAL_MS = 2000;

/** Roughly two poll intervals — see the module header for why. */
export const GUARD_TTL_MS = POLL_INTERVAL_MS * 2;

/**
 * The ceiling on how long a still-PENDING write may hold its guard.
 *
 * `GUARD_TTL_MS` is the right window for a write that has already landed —
 * it only has to outlast a poll fetch that was in flight when the write
 * committed. It is the wrong window for a write that has not landed yet:
 * a `deleteDoc` slower than 4 s let the guard lapse while the document was
 * still on the server, so the next poll re-merged it and the removed row
 * REAPPEARED, then vanished again once the delete finally landed — exactly
 * the resurrection this subsystem exists to prevent.
 *
 * An optimistic-remove thunk arms the guard with this value and re-arms it
 * down to `GUARD_TTL_MS` the moment the write settles, so the guard's
 * lifetime tracks the operation rather than a wall-clock guess. This
 * constant is only ever reached by a promise that never settles at all —
 * with Firestore's offline write queue that is a real state, not a
 * hypothetical, which is why the guard is bounded here instead of held
 * forever.
 */
export const GUARD_MAX_PENDING_MS = 60000;

/**
 * Starts a single local-mode poll loop, parameterized over the fetch and
 * delivery callbacks. It only has one caller (`keyQuestions`), but stays
 * parameterized rather than inlined into its one call site:
 * `chatListeners.ts` already carries three near-identical copies of this
 * shape, and this module must not regress to a fourth, hand-rolled one the
 * moment a second mutable channel exists again.
 *
 * Reproduces `chatListeners.ts:138-162`'s exact mechanics: a mutable
 * `isActive` flag, an async `poll()` that returns immediately when
 * `!isActive`, awaits `fetchFn()` inside `try`, re-checks `isActive` before
 * calling `onDocs`, routes a caught error to `onError` without stopping the
 * loop (a poll error must never kill future ticks), and re-arms with
 * `setTimeout(poll, POLL_INTERVAL_MS)` while still active. `poll()` is
 * called once immediately so the first fetch does not wait a full interval.
 * Returns an unsubscribe closure — `() => { isActive = false }` — for the
 * caller to register with `advisoryListenerManager` under the SAME key the
 * `onSnapshot` branch would have used, so every existing teardown path
 * (`cleanupKeyQuestionsListener`, `stopEngagementListeners`, cross-project
 * teardown, the logout teardown) keeps working with zero additional
 * wiring.
 */
export function startLocalPoll<TRaw>(params: {
  fetchFn: () => Promise<TRaw[]>;
  onDocs: (docs: TRaw[]) => void;
  onError: (error: unknown) => void;
}): () => void {
  const { fetchFn, onDocs, onError } = params;
  let isActive = true;
  // The scheduled re-arm's handle, so unsubscribe can actually cancel it.
  // Flipping `isActive` alone only makes the already-scheduled callback
  // early-return — it still fires up to one full interval after teardown,
  // holding this closure and everything it captures (the manager key, the
  // dispatch-bound callbacks) alive until then, and leaving a pending
  // timer behind in any fake-timer test that tears down mid-interval.
  let timer: ReturnType<typeof setTimeout> | null = null;

  const poll = async (): Promise<void> => {
    if (!isActive) {
      return;
    }
    try {
      const docs = await fetchFn();
      if (isActive) {
        onDocs(docs);
      }
    } catch (error) {
      // The SAME teardown re-check the success path above already
      // performs. `onError` resolves `advisoryListenerManager.markError(key,
      // …)` against the listener KEY, not against this poll instance, and
      // `register()` is remove-then-set under that key — so a torn-down
      // poll's late rejection would otherwise land on whatever listener now
      // holds the key. The concrete path: "Try again" unsubscribes poll #1
      // and registers poll #2 under the identical key, then poll #1's
      // in-flight `getDocs` rejection — the very rejection the user is
      // retrying — flips poll #2's channel to `'failed'`.
      if (isActive) {
        onError(error);
      }
    }
    if (isActive) {
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    }
  };

  poll();

  return () => {
    isActive = false;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };
}
